import { useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import DrawerSection from '../common/Drawer/DrawerSection'
import DrawerCard from '../common/Drawer/DrawerCard'
import DrawerAddButton from '../common/Drawer/DrawerAddButton'
import DrawerPicker from '../common/Drawer/DrawerPicker'
import DrawerEmptyState from '../common/Drawer/DrawerEmptyState'
import Select from '../common/form/Select'
import { adminUpdateCoach } from '../../actions/coachActions'

const CoachDrawerOrganizations = ({ coach }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const organizations = useSelector(state => state.organizations.organizations)
  const [isSaving, setIsSaving] = useState(false)
  const [isAdding, setIsAdding] = useState(false)

  const coachOrganizations = coach.organizations || []
  const directOrganizationIds = coachOrganizations.filter(organization => organization.direct).map(organization => organization.id)
  const options = organizations
    .filter(organization => !coachOrganizations.some(current => current.id === organization.id))
    .map(organization => ({ value: organization.id, label: organization.name }))

  const saveOrganizations = (organizationIds) => {
    setIsSaving(true)
    dispatch(adminUpdateCoach({ id: coach.id, organization_ids: organizationIds }))
      .then(() => {
        toast.success(t('coaches.drawer.organizationsUpdated'), { position: toast.POSITION.TOP_RIGHT })
        setIsAdding(false)
      })
      .catch(() => toast.error(t('coaches.drawer.organizationsUpdateError'), { position: toast.POSITION.TOP_RIGHT }))
      .finally(() => setIsSaving(false))
  }

  return (
    <DrawerSection title={t('coaches.drawer.organizations')} hint={t('coaches.drawer.organizationsHint')}>
      {coachOrganizations.length === 0 && <DrawerEmptyState>{t('coaches.drawer.noOrganizations')}</DrawerEmptyState>}

      {coachOrganizations.map(organization => (
        <DrawerCard
          key={organization.id}
          highlight
          icon={<i className='fa fa-users' aria-hidden='true' />}
          label={organization.name}
          note={organization.direct
            ? t('coaches.drawer.directMembership')
            : t('coaches.drawer.viaCommunity', { names: organization.community_names.join(', ') })}
          onRemove={organization.direct ? () => saveOrganizations(directOrganizationIds.filter(id => id !== organization.id)) : undefined}
          disabled={isSaving}
        />
      ))}

      {isAdding ? (
        <DrawerPicker title={t('coaches.drawer.chooseOrganization')} onCancel={() => setIsAdding(false)}>
          <Select
            inputId='coachDrawerOrganization'
            value={null}
            options={options}
            onChange={(option) => saveOrganizations([...directOrganizationIds, option.value])}
            placeholder={t('coaches.drawer.searchOrganizations')}
            noOptionsMessage={() => t('coaches.invite.noOrganizationsFound')}
            isDisabled={isSaving}
            menuPlacement='auto'
          />
        </DrawerPicker>
      ) : options.length > 0 && (
        <DrawerAddButton onClick={() => setIsAdding(true)} disabled={isSaving}>{t('coaches.drawer.addOrganization')}</DrawerAddButton>
      )}
    </DrawerSection>
  )
}

CoachDrawerOrganizations.propTypes = {
  coach: PropTypes.shape({
    id: PropTypes.number.isRequired,
    organizations: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      direct: PropTypes.bool.isRequired,
      community_names: PropTypes.arrayOf(PropTypes.string).isRequired,
    })),
  }).isRequired,
}

export default CoachDrawerOrganizations
